import { t } from "i18next";
import { DateTime } from "luxon";

import { IAppointmentEntryEX } from "~/web-shared-components/interfaces/IAppointmentEntryEX";

import { AsnNetDatabaseContact } from "../../web-shared-components/asn1/EUCSrv/stubs/ENetUC_Common";
import { IAsnAppointmentEntry } from "../../web-shared-components/interfaces/serializable/IAsnAppointmentEntry";
import { getInitials } from "./commonHelper";

interface ICombiPic {
	initials: string;
	jpegPhoto: string;
	visible: boolean;
}

/**
 *
 */
export default class ContactHelper {
	/**
	 * Gets the avatar image
	 * @param databaseContact - the asn database contact
	 * @returns - a string containing the encoded jpeg
	 */
	public static getAvatarImage = (databaseContact: AsnNetDatabaseContact): string => {
		let imageData = "";
		if (databaseContact && databaseContact.optionalParams) {
			const optionalParams = databaseContact.optionalParams;

			for (const [key, value] of Object.entries(optionalParams)) {
				if (key === "jpegPhoto") {
					for (const [innerKey, innerValue] of Object.entries(value))
						if (innerKey === "binarydata") imageData = innerValue as string;
				}
			}
			return imageData;
		} else return "";
	};

	/**
	 * Get picture for a contact
	 * @param databaseContact - the AsnNetDatabaseContact
	 * @returns - an ICombiPic object
	 */
	public static getPic(databaseContact?: AsnNetDatabaseContact): ICombiPic {
		let pic: ICombiPic = {
			initials: "",
			jpegPhoto: "",
			visible: false
		};
		if (databaseContact) {
			const jpegPhoto = ContactHelper.getAvatarImage(databaseContact);
			pic = {
				initials: getInitials(databaseContact.u8sDisplayName || "", ""),
				jpegPhoto,
				visible: jpegPhoto !== ""
			};
		}
		return pic;
	}

	/**
	 * Helper to get the current valid appointments as an array of IAppointmentEntryEX
	 * the extended interface contains the enriched text to describe the appointment
	 * @param appointments - the appointments of the contact
	 * @param maxResults - if we want to limit the results of appointments
	 * @returns an of IAppointmentEntryEX containing the current valid appointments
	 */
	public static getCurrentValidAppointments = (
		appointments: IAsnAppointmentEntry[] | undefined,
		maxResults?: number
	): IAppointmentEntryEX[] => {
		if (!appointments) return [];
		const validAppointments: IAsnAppointmentEntry[] = [];
		const enrichedValidAppointments: IAppointmentEntryEX[] = [];
		for (const [index, appointment] of appointments.entries())
			if (appointment?.bIsValid) if (!maxResults || index < maxResults) validAppointments.push(appointment); // selecting only valid appointments

		if (validAppointments && validAppointments.length > 0) {
			for (const validAppointment of validAppointments) {
				let currentAppointmentString = "";
				const stCurStart = DateTime.fromISO(validAppointment.stStart as unknown as string);
				const stCurEnd = DateTime.fromISO(validAppointment.stEnd as unknown as string);
				let startCurTime = stCurStart.toFormat("HH:mm");
				let endCurTime = stCurEnd.toFormat("HH:mm");
				const isCurStartToday = stCurStart.hasSame(DateTime.now(), "day");
				const isCurEndToday = stCurEnd.hasSame(DateTime.now(), "day");
				const enrichedAppointment: IAppointmentEntryEX = {
					...validAppointment
				};
				// all day event
				if (validAppointment.bAllDay) {
					const weekDay = stCurStart.weekdayLong;
					currentAppointmentString =
						weekDay + "," + " " + stCurStart.toFormat("dd/MM/yyyy") + " " + t("IDS_APPOINTMENT_ALLDAY");
					enrichedAppointment.enrichedDescription = currentAppointmentString;
				} else {
					// today, and also only if start and end of the meeting is in the same day
					if (isCurStartToday && isCurEndToday)
						currentAppointmentString = t("IDS_APPOINTMENT_DURATION", { starttime: startCurTime, endtime: endCurTime });
					else {
						const startWeekDay = stCurStart.weekdayLong;
						const endWeekDay = stCurEnd.weekdayLong;
						const tomorrow = DateTime.now().plus({ hours: 24 });
						// tomorrow
						if (stCurStart <= tomorrow) {
							startCurTime = startWeekDay + "," + " " + stCurStart.toFormat("dd/MM/yyyy") + " " + startCurTime;
							endCurTime = endWeekDay + "," + " " + stCurEnd.toFormat("dd/MM/yyyy") + " " + endCurTime;
							currentAppointmentString = t("IDS_APPOINTMENT_DURATION", {
								starttime: startCurTime,
								endtime: endCurTime
							});
						} else currentAppointmentString = t("IDS_APPOINTMENT_NO_NEXT24HRS");
					}
					enrichedAppointment.enrichedDescription = currentAppointmentString;
				}
				enrichedValidAppointments.push(enrichedAppointment);
			}
		}
		return enrichedValidAppointments;
	};

	/**
	 * Helper to get the next appointment as a translated string
	 * @param nextAppointment - the next appointment of a contact
	 * @param isThereAnyCurrentAppointment - if there is any current appointment
	 * @returns a string containing the information
	 */
	public static getNextAppointment = (
		nextAppointment: IAsnAppointmentEntry | undefined,
		isThereAnyCurrentAppointment?: boolean
	): string => {
		if (!nextAppointment) return "";
		if (!nextAppointment.bIsValid)
			// invalid appointment ...
			return ""; // will result in empty string

		let nextAppointmentString = "";
		const stCurStart = DateTime.fromISO(nextAppointment?.stStart as unknown as string);
		const stCurEnd = DateTime.fromISO(nextAppointment?.stEnd as unknown as string);
		let startCurTime = stCurStart.toFormat("HH:mm");
		let endCurTime = stCurEnd.toFormat("HH:mm");
		const isCurStartToday = stCurStart.hasSame(DateTime.now(), "day");
		const isCurEndToday = stCurEnd.hasSame(DateTime.now(), "day");

		// all day event
		if (nextAppointment.bAllDay) {
			const weekDay = stCurStart.weekdayLong;
			nextAppointmentString =
				weekDay + "," + " " + stCurStart.toFormat("dd/MM/yyyy") + " " + t("IDS_APPOINTMENT_ALLDAY");
		} else {
			// today, and also only if start and end of the meeting is in the same day
			if (isCurStartToday && isCurEndToday)
				nextAppointmentString = t("IDS_APPOINTMENT_DURATION", { starttime: startCurTime, endtime: endCurTime });
			else {
				const startWeekDay = stCurStart.weekdayLong;
				const endWeekDay = stCurEnd.weekdayLong;
				const tomorrow = DateTime.now().plus({ hours: 24 });
				// tomorrow
				if (stCurStart <= tomorrow) {
					startCurTime = startWeekDay + "," + " " + stCurStart.toFormat("dd/MM/yyyy") + " " + startCurTime;
					endCurTime = endWeekDay + "," + " " + stCurEnd.toFormat("dd/MM/yyyy") + " " + endCurTime;
					nextAppointmentString = t("IDS_APPOINTMENT_DURATION", { starttime: startCurTime, endtime: endCurTime });
				} else {
					if (isThereAnyCurrentAppointment) nextAppointmentString = t("IDS_APPOINTMENT_NO_FURTHER_NEXT24HRS");
					else nextAppointmentString = t("IDS_APPOINTMENT_NO_NEXT24HRS");
				}
			}
		}
		return nextAppointmentString;
	};
}
